import React, { useMemo } from 'react';
import './OverviewBars.css';

interface MetricInfo {
  metric: string;
  label: string;
}

interface ItemData {
  name: string;
  label: string;
  campus?: string;
  values: Record<string, number>;
}

interface CourseInfo {
  course: string;
  label: string;
}

interface Scope {
  kind: string;
  label: string;
}

export interface OverviewBarsData {
  type: 'overview-bars';
  title: string;
  subtitle?: string;
  course?: CourseInfo;
  dimension?: string;
  items: ItemData[];
  metrics: MetricInfo[];
  scope?: Scope;
  question?: string;
}

const METRIC_COLORS = ['#5b8fb9', '#6366f1', '#7be495', '#f6c85f', '#c490d1', '#6dd3e3', '#f87171', '#fb923c'];

const OverviewBars: React.FC<{ data: OverviewBarsData }> = ({ data }) => {
  const { title, subtitle, items, metrics, scope, course } = data;

  const maxPerMetric = useMemo(() => {
    const maxMap: Record<string, number> = {};
    for (const m of metrics) {
      let max = 0;
      for (const item of items) {
        const val = item.values[m.metric] ?? 0;
        if (val > max) max = val;
      }
      maxMap[m.metric] = max || 1;
    }
    return maxMap;
  }, [items, metrics]);

  const metricColors = useMemo(() => {
    const map: Record<string, string> = {};
    metrics.forEach((m, idx) => {
      map[m.metric] = METRIC_COLORS[idx % METRIC_COLORS.length];
    });
    return map;
  }, [metrics]);

  return (
    <div className="overview-bars">
      <div className="ob-header">
        {course && <span className="ob-course-label">{course.label}</span>}
        <h2 className="ob-title">{scope?.label || title}</h2>
        {subtitle && <p className="ob-subtitle">{subtitle}</p>}
      </div>

      <div className="ob-legend">
        {metrics.map(m => (
          <span className="ob-legend-item" key={m.metric}>
            <span className="ob-legend-swatch" style={{ background: metricColors[m.metric] }} />
            {m.label || m.metric}
          </span>
        ))}
      </div>

      <div className="ob-items">
        {items.map((item, idx) => (
          <div className="ob-item" key={idx}>
            <div className="ob-item-header">
              <span className="ob-item-name">{item.name || item.label}</span>
              {item.campus && <span className="ob-item-campus">{item.campus}</span>}
            </div>
            <div className="ob-item-metrics">
              {metrics.map(m => {
                const val = item.values[m.metric] ?? 0;
                const widthPct = (val / maxPerMetric[m.metric]) * 100;
                return (
                  <div className="ob-metric-cell" key={m.metric}>
                    <span className="ob-metric-name">{m.label || m.metric}</span>
                    <div className="ob-metric-bar-row">
                      <div className="ob-metric-track">
                        <div
                          className="ob-metric-fill"
                          style={{
                            width: `${widthPct}%`,
                            background: metricColors[m.metric],
                          }}
                        />
                      </div>
                      <span className="ob-metric-value">{val.toLocaleString('es-ES')}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OverviewBars;
